import { useEffect, useRef, useCallback } from 'react';
import { useVideoPlayerStore } from '@/store/videoPlayerStore';
import { useTrackListeningTime, type MilestoneCheckCallback } from './useListeningTime';
import { listeningTimeService } from '@/services/listeningTime.service';

interface UseYouTubeListeningTrackerProps {
  /** ISO language code for the project being studied. Tracking is skipped when undefined. */
  language: string | undefined;
  /**
   * Must be `true` before tracking starts.
   * Set to `false` while the project is loading to prevent spurious tracking
   * before the language is known.
   */
  enabled: boolean;
  onMilestoneCheck?: MilestoneCheckCallback;
}

/**
 * useYouTubeListeningTracker — records YouTube study time using videoPlayerStore.playerState.
 *
 * Architecture rules (from architecture.md):
 * - READ-ONLY from videoPlayerStore — this hook NEVER writes to the store.
 * - Records ONLY when `playerState === 'playing'` (positive check, never a blocklist).
 * - Mirrors the AudioPlayer 30-second accumulator pattern exactly.
 * - 30s periodic interval sends accumulated time; flush on unmount uses the direct service
 *   (not the TanStack mutation) because mutations cannot be called inside React cleanup.
 *
 * This hook is safe to mount on both VideoPlayer.tsx (main playback) and
 * YouTubeSegmentModal.tsx (vocab review playback) because both contexts write to the
 * same videoPlayerStore.playerState and are never active simultaneously.
 */
export function useYouTubeListeningTracker({ language, enabled, onMilestoneCheck }: UseYouTubeListeningTrackerProps) {
  const playerState = useVideoPlayerStore((s) => s.playerState);
  const { mutate: trackListeningTime } = useTrackListeningTime({ onMilestoneCheck });

  // Refs: no re-renders needed for tracking state
  const playStartTimeRef = useRef<number | null>(null);
  const accumulatedSecondsRef = useRef<number>(0);
  const trackingIntervalRef = useRef<number | null>(null);

  // Always-current language ref for the unmount-flush closure
  const languageRef = useRef(language);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  // Always-current playerState ref so the interval can read it without being in its deps
  const playerStateRef = useRef(playerState);
  useEffect(() => {
    playerStateRef.current = playerState;
  }, [playerState]);

  // Always-current enabled ref for the same reason
  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Send accumulated seconds via TanStack mutation (used during active playback)
  const sendListeningTime = useCallback(() => {
    const seconds = Math.floor(accumulatedSecondsRef.current);
    if (seconds > 0 && language) {
      console.log('[YouTubeListeningTracker] Sending listening time:', seconds, 'seconds for language:', language);
      trackListeningTime({ seconds, language, media_type: 'youtube' });
      accumulatedSecondsRef.current = 0;
    }
  }, [trackListeningTime, language]);

  // Effect 1: playerState transition handler
  // - When state enters 'playing': capture the start timestamp.
  // - When state leaves 'playing': accumulate elapsed time, clear the start timestamp.
  useEffect(() => {
    if (!enabled) return;

    if (playerState === 'playing') {
      if (playStartTimeRef.current === null) {
        console.log('[YouTubeListeningTracker] Tracking started');
        playStartTimeRef.current = performance.now();
      }
    } else {
      // Covers: 'paused', 'buffering', 'ad', 'ended', 'idle'
      if (playStartTimeRef.current !== null) {
        const elapsed = (performance.now() - playStartTimeRef.current) / 1000;
        accumulatedSecondsRef.current += elapsed;
        playStartTimeRef.current = null;
        console.log('[YouTubeListeningTracker] Tracking paused. Accumulated:', accumulatedSecondsRef.current, 's');
      }
    }
  }, [playerState, enabled]);

  // Effect 2: 30-second periodic interval — stable for the lifetime of the component.
  // Reads playerState and enabled via refs so the interval is never torn down by state
  // transitions (e.g. short-segment loops that cycle through 'ended' → 'playing' faster
  // than 30 seconds, which previously reset the countdown before it could ever fire).
  useEffect(() => {
    if (!enabled) return;

    trackingIntervalRef.current = window.setInterval(() => {
      if (!enabledRef.current) return;
      // Checkpoint any in-progress play time into the accumulator
      if (playerStateRef.current === 'playing' && playStartTimeRef.current !== null) {
        const elapsed = (performance.now() - playStartTimeRef.current) / 1000;
        accumulatedSecondsRef.current += elapsed;
        playStartTimeRef.current = performance.now(); // reset checkpoint
      }
      console.log('[YouTubeListeningTracker] Interval tick. Accumulated:', accumulatedSecondsRef.current, 's');
      if (accumulatedSecondsRef.current >= 30) {
        sendListeningTime();
      }
    }, 30000);

    return () => {
      if (trackingIntervalRef.current !== null) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Stable interval: only mounts once when enabled becomes true. playerState is read
  // via ref to avoid resetting the 30s countdown on every state change (segment loops).
  }, [enabled, sendListeningTime]);

  // Effect 3: Flush on unmount — runs exactly once during cleanup.
  // Must use listeningTimeService.track() directly; useMutation cannot be called in cleanup.
  useEffect(() => {
    return () => {
      // Accumulate any remaining elapsed time
      if (playStartTimeRef.current !== null) {
        const elapsed = (performance.now() - playStartTimeRef.current) / 1000;
        accumulatedSecondsRef.current += elapsed;
        playStartTimeRef.current = null;
      }
      // Clear any active interval
      if (trackingIntervalRef.current !== null) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
      // Flush remaining via direct service call
      const seconds = Math.floor(accumulatedSecondsRef.current);
      const lang = languageRef.current;
      if (seconds > 0 && lang) {
        console.log('[YouTubeListeningTracker] Unmount flush:', seconds, 'seconds for language:', lang);
        listeningTimeService.track({ seconds, language: lang, media_type: 'youtube' });
        accumulatedSecondsRef.current = 0;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Empty deps: intentional — this is a mount/unmount effect only; refs are always current
  }, []);
}
